import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { TextField } from '@/design-system/atoms/Forms/TextField';
import { Typeahead } from '@/design-system/atoms/Forms/Typeahead';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconSearch } from '@/design-system/atoms/Icons/IconSearch/Lazy';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import IllustrationPlayer from '@/design-system/molecules/IllustrationPlayer';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { trackInteraction } from '@/utils/analytics';
import { capitalizeWords } from '@/utils/stringFormat';

import { useSiteSearchContext } from '../../contexts/SiteSearchContext';
import styles from './SearchHero.module.scss';

/**
 * The SearchHero component contains the breadcrumb and search controls
 * for the default search page.
 * @param {object} props - The component props
 * @param {array} props.breadcrumbs - The breadcrumb items
 * @param {string} props.className - Additional class name
 * @param {string} props.initialSearchValue - The initial search value
 * @param {string} props.searchLabel - The input search label
 * @param {string} props.title - The page title
 * @param {object} props.validationError - The validation error label
 * @returns {React.Component} The search hero component
 */
const SearchHero = React.forwardRef(
    (
        {
            breadcrumbs,
            className,
            illustration,
            initialSearchValue,
            searchLabel,
            title,
            validationError,
            ...props
        },
        ref,
    ) => {
        const {
            coveo: {
                controller: { searchBox },
            },
            triggerExecuteSearch,
        } = useSiteSearchContext();
        const breakpoint = useBreakpoint();

        const searchRef = React.useRef();

        const [searchValue, setSearchValue] = React.useState(initialSearchValue);
        const [searchValueInvalid, setSearchValueInvalid] = React.useState(false);
        const [searchValueError, setSearchValueError] = React.useState();
        const [typeaheadItems, setTypeaheadItems] = React.useState([]);

        /**
         * Handle submit form event
         * @param {Event} e - The event
         * @returns {void}
         */
        const submitForm = (e) => {
            e.preventDefault();
            let hasError = false;

            if (!searchValue) {
                setSearchValueInvalid(true);
                setSearchValueError(validationError);
                searchRef.current.focus();
                hasError = true;
            }

            if (hasError) {
                return;
            }

            setSearchValueInvalid(false);

            // Analytics
            trackInteraction({
                eventName: 'search',
                componentName: 'Search Hero',
                componentTitle: title,
                interactionType: 'search_results',
                selector: 'search',
                searchTerm: searchValue,
            });

            triggerExecuteSearch({
                searchValue,
                analyticsActionCreator: {
                    event: 'searchboxSubmit',
                    payload: {},
                },
            });
        };

        /**
         * Update search value if a typeahead item is selected
         * @param {string} value - The selected item's value
         * @returns {void}
         */
        const handleSetSearchValue = React.useCallback(
            (value) => {
                if (value.length >= 2) {
                    searchBox.updateText(value);
                } else {
                    searchBox.clear();
                }

                setSearchValue(value);
            },
            [searchBox],
        );

        /**
         * Trigger a search if a typeahead item is selected
         * @param {object} item - The selected item
         * @returns {void}
         */
        const handleSelectSearchValue = React.useCallback(
            (item) => {
                if (item === null) {
                    searchBox.clear();
                    return;
                }

                // Analytics
                trackInteraction({
                    eventName: 'search',
                    componentName: 'Search Hero',
                    componentTitle: title,
                    interactionType: 'predictive_search_click',
                    actionLabel: item.text,
                    selector: 'search',
                    searchTerm: searchValue,
                });

                triggerExecuteSearch({
                    searchValue: item.text,
                    analyticsActionCreator: {
                        event: 'querySuggestionClick',
                        payload: {
                            id: 'site-search-searchbox',
                            suggestion: item?.rawValue,
                        },
                    },
                });
            },
            [triggerExecuteSearch, searchBox, searchValue, title],
        );

        // Reset the search value error if the search value is updated
        React.useEffect(() => {
            if (searchValue) {
                setSearchValueInvalid(false);
            }
        }, [searchValue]);

        // Execute the search if an initialSearchValue is provided
        React.useEffect(() => {
            if (!initialSearchValue) {
                return;
            }

            setSearchValue(initialSearchValue);
            triggerExecuteSearch({ searchValue: initialSearchValue });
        }, [initialSearchValue, triggerExecuteSearch]);

        // Populate the typeahead items from coveo suggestions
        React.useEffect(() => {
            return searchBox?.subscribe(() => {
                const newTypeaheadItems = searchBox.state.suggestions.map((s, i) => ({
                    id: i,
                    text: capitalizeWords(s.rawValue),
                    rawValue: s.rawValue,
                }));

                setTypeaheadItems(newTypeaheadItems);
            });
        }, [searchBox]);

        return (
            <Theme name={Theme.NAME.MEDIUM}>
                <ContentContainer
                    className={classnames(className, styles['search-hero'])}
                    {...props}
                    ref={ref}
                >
                    {breadcrumbs.length > 0 && (
                        <Breadcrumb
                            className={styles['search-hero__breadcrumb']}
                            items={breadcrumbs}
                            analytics={{
                                eventName: 'search',
                                componentName: 'Search Hero',
                                componentTitle: title,
                                searchTerm: searchValue,
                            }}
                        />
                    )}
                    <Grid
                        className={classnames(
                            styles['search-hero__grid'],
                            !breakpoint?.medium && styles['search-hero__grid--hidden-illustration'],
                        )}
                    >
                        <GridColumn
                            colSpan={{ sm: 4, md: 4, lg: 7 }}
                            className={styles['search-hero__content']}
                        >
                            <Title
                                className={styles['search-hero__title']}
                                variant={Title.VARIANT.H2}
                                tag={Title.TAG.H1}
                                content={title}
                            />
                            <form
                                className={classnames(
                                    styles['search-hero__form'],
                                    searchValueInvalid && styles['search-hero__form--invalid'],
                                )}
                                onSubmit={submitForm}
                                data-trigger="search"
                            >
                                <Typeahead
                                    ref={searchRef}
                                    label={searchLabel}
                                    variant={TextField.VARIANT.BOTTOM_BORDER}
                                    buttonProps={undefined}
                                    items={typeaheadItems}
                                    className={styles['search-hero__typeahead']}
                                    inputValue={searchValue}
                                    inputType="search"
                                    onInputChange={handleSetSearchValue}
                                    onSelectionItemChange={handleSelectSearchValue}
                                    isInvalid={searchValueInvalid}
                                    validationErrors={searchValueError}
                                />
                                <div className={styles['search-hero__separator']} />
                                <div className={styles['search-hero__button-wrapper']}>
                                    <Button
                                        ariaLabel="Submit Search"
                                        className={styles['search-hero__button']}
                                        type="submit"
                                        iconComponent={LazyIconSearch}
                                    />
                                </div>
                            </form>
                        </GridColumn>
                        {!breakpoint?.medium ? null : (
                            <GridColumn
                                colStart={{ sm: 5, md: 5, lg: 10 }}
                                colSpan={{ sm: 3, md: 3, lg: 3 }}
                                className={styles['search-hero__illustration']}
                            >
                                {illustration?.scene && illustration?.title && (
                                    <IllustrationPlayer {...illustration} />
                                )}
                            </GridColumn>
                        )}
                    </Grid>
                </ContentContainer>
            </Theme>
        );
    },
);

SearchHero.propTypes = {
    breadcrumbs: Breadcrumb.propTypes.items,
    className: PropTypes.string,
    illustration: PropTypes.object,
    initialSearchValue: PropTypes.string,
    onSubmit: PropTypes.func,
    searchLabel: PropTypes.string,
    title: PropTypes.string,
    validationError: PropTypes.string,
};

SearchHero.defaultProps = {
    breadcrumbs: [],
    initialSearchValue: '',
    searchLabel: 'Search',
    title: 'Search',
};

export default SearchHero;
